import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { buildProgrammGraph, FLOW_EDGES, NODE_H, NODE_W, type GraphNode } from './graph';

/** Liegt diese Datei unter packages/ui/public/, wird sie statt des Platzhalters angezeigt. */
export const PROGRAMMUEBERSICHT_SVG_URL = '/programmuebersicht.svg';

const PLACEHOLDER_HINT = 'Platzhalter – die finale Grafik folgt';

/** Bricht Text grob nach Zeichenzahl um (SVG kennt keinen automatischen Umbruch). */
function wrap(text: string, max = 34): string[] {
  const lines: string[] = [];
  let line = '';
  for (const word of text.split(' ')) {
    if (line && (line + ' ' + word).length > max) { lines.push(line); line = word; }
    else line = line ? `${line} ${word}` : word;
  }
  if (line) lines.push(line);
  return lines;
}

/** Pfeil von Knoten a nach Knoten b (rechts→links bzw. unten→oben). */
function edgePath(a: GraphNode, b: GraphNode): string {
  if (a.x === b.x) {
    const x = a.x + NODE_W / 2;
    const [y1, y2] = b.y > a.y ? [a.y + NODE_H, b.y] : [a.y, b.y + NODE_H];
    return `M ${x} ${y1} L ${x} ${y2}`;
  }
  const forward = b.x > a.x;
  const x1 = forward ? a.x + NODE_W : a.x;
  const x2 = forward ? b.x : b.x + NODE_W;
  const y1 = a.y + NODE_H / 2;
  const y2 = b.y + NODE_H / 2;
  const mid = (x1 + x2) / 2;
  return `M ${x1} ${y1} C ${mid} ${y1}, ${mid} ${y2}, ${x2} ${y2}`;
}

function Platzhaltergrafik(): ReactElement {
  const graph = useMemo(() => buildProgrammGraph(), []);
  const byKey = new Map(graph.nodes.map((n) => [n.key, n]));
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <svg
        role="img" aria-label="Programmübersicht: Seiten des Projektmenüs und ihre Zusammenhänge"
        viewBox={`0 0 ${graph.width} ${graph.height}`} width="100%" style={{ minWidth: 900 }}
      >
        <defs>
          <marker id="pu-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#546e7a" />
          </marker>
        </defs>
        {graph.columns.map((c) => (
          <text key={c.key} x={c.x} y={30} fontSize={16} fontWeight={700} fill="#263238">{c.title}</text>
        ))}
        {FLOW_EDGES.map((e) => {
          const a = byKey.get(e.from);
          const b = byKey.get(e.to);
          if (!a || !b) return null;
          return <path key={`${e.from}-${e.to}`} d={edgePath(a, b)} fill="none" stroke="#546e7a" strokeWidth={2} markerEnd="url(#pu-arrow)" />;
        })}
        {graph.nodes.map((n) => (
          <g key={n.key}>
            <rect x={n.x} y={n.y} width={NODE_W} height={NODE_H} rx={8} fill="#eceff1" stroke="#90a4ae" />
            <text x={n.x + 12} y={n.y + 24} fontSize={14} fontWeight={700} fill="#102027">{n.title}</text>
            {wrap(n.description).map((line, i) => (
              <text key={i} x={n.x + 12} y={n.y + 44 + i * 16} fontSize={11.5} fill="#37474f">{line}</text>
            ))}
          </g>
        ))}
      </svg>
    </Box>
  );
}

/** Programmübersicht: zeigt die gelieferte SVG, solange keine vorliegt den Platzhalter. */
export function Programmuebersicht(): ReactElement {
  const [svgAvailable, setSvgAvailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(PROGRAMMUEBERSICHT_SVG_URL, { method: 'HEAD' })
      .then((res) => {
        // Der SPA-Fallback liefert für unbekannte Pfade HTML mit Status 200 – deshalb Content-Type prüfen.
        const type = res.headers.get('content-type') ?? '';
        if (!cancelled) setSvgAvailable(res.ok && type.includes('svg'));
      })
      .catch(() => { if (!cancelled) setSvgAvailable(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>Programmübersicht</Typography>
      {svgAvailable ? (
        <Box component="img" src={PROGRAMMUEBERSICHT_SVG_URL} alt="Programmübersicht" sx={{ width: '100%', height: 'auto' }} />
      ) : (
        <>
          <Alert severity="warning" sx={{ mb: 2, fontWeight: 600 }}>{PLACEHOLDER_HINT}</Alert>
          <Platzhaltergrafik />
        </>
      )}
    </Box>
  );
}
